import {
  // eslint-disable-next-line max-len
  BranchCommit,
  CommitSummaryEntry,
  DeploymentHistoryEntry,
  DeploymentTag,
  TagDetails,
  LineTestRun,
  LinkTag,
  PrivateBranch,
  PrivateBranches,
  PublicBranch,
  PublicBranches, UsersList,
} from '../../../dtos/branches.dto';
import { axiosAuthClient } from '../../auth/axiosAuthClient';
import {
  addMocks, APIRequest, formatRequestParams, Paginated,
} from '../../types';
import Mocks from './mocks';
import {
  BranchesAPI,
  GetDeploymentTagsParams,
  GetDeploymentTagsQueryParams,
} from './types';
import { getFile } from '../../../utils/getFile';

const defaultUrl = '/apiRes';
const v1Path = '/v1';

const formatGetDeploymentTagsParams = ({
  branchName,
  params,
}: GetDeploymentTagsParams): GetDeploymentTagsQueryParams => ({
  branchName,
  offset: params.offset,
  limit: params.limit,
  tagCreationDateFrom: params.filters?.find((filter) => filter.field === 'creationDate' && filter.operation === 'gt')?.value,
  tagCreationDateTo: params.filters?.find((filter) => filter.field === 'creationDate' && filter.operation === 'lt')?.value,
  tagName: params.filters?.find((filter) => filter.field === 'name')?.value
});

export const getBranchesAPI = (baseUrl = defaultUrl) => {
  const defaultV1Url = `${baseUrl}${v1Path}`;

  const api: BranchesAPI = {
    getPublicBranches: new APIRequest(
      () => axiosAuthClient.get<PublicBranches>(`${defaultV1Url}/branches?isPublic=true`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches', method: 'get' },
    ),
    createPublicBranch: new APIRequest(
      (params) => axiosAuthClient.post<PublicBranch>(`${defaultV1Url}/branches`, params).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches', method: 'post' },
    ),
    deletePublicBranch: new APIRequest(
      (branchName) => axiosAuthClient.delete(`${defaultV1Url}/branches/${encodeURI(branchName)}`).then((() => {})),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    importPublicBranch: new APIRequest(
      (params) => {
        const formData = new FormData();
        formData.append('file', params.file);
        return axiosAuthClient.post(`${defaultV1Url}/branches/${params.branchName}/import`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/import', method: 'put' },
    ),
    exportPublicBranch: new APIRequest(
      (publicBranchName) => axiosAuthClient.get(`${defaultV1Url}/branches/${publicBranchName}/export`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/export', method: 'get' },
    ),
    exportReport: new APIRequest(
      (branchName) => axiosAuthClient.get(`${defaultV1Url}/branches/${branchName}/enquiryReport`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/enquiryReport', method: 'get' },
    ),
    getPrivateBranches: new APIRequest(
      (params) => axiosAuthClient.get<PrivateBranches>(`${defaultV1Url}/branches/${params.publicBranchName}/privateBranches`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/privateBranches', method: 'get' },
    ),
    createPrivateBranch: new APIRequest(
      (params) => axiosAuthClient.post<PrivateBranch>(`${defaultV1Url}/branches/${params.forkedFromBranchName}/privateBranches`, { name: params.name }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/privateBranches', method: 'post' },
    ),
    getBranchAssignees: new APIRequest(
      () => axiosAuthClient.get<UsersList>(`${defaultV1Url}/users/listByPermission?permission=CAN_CREATE_WORK_BRANCH&excludeCurrentUser=true`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/users\\/listByPermission', method: 'get' },
    ),
    assignPrivateBranch: new APIRequest(
      (params) => axiosAuthClient.post(`${defaultV1Url}/branches/${params.branchName}/assign`, { username: params.username }),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/assign', method: 'post' },
    ),
    getPrivateBranchesHistory: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<BranchCommit>>(`${defaultV1Url}/branches/${params.branchName}/history`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/history', method: 'get' },
    ),
    exportPrivateBranch: new APIRequest(
      (privateBranchName) => axiosAuthClient.get(`${defaultV1Url}/branches/${privateBranchName}/export`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/export', method: 'get' },
    ),
    pullBranch: new APIRequest(
      (params) => axiosAuthClient.post(`${defaultV1Url}/branches/${params.toBranchName}/pull`, { fromBranchName: params.fromBranchName, resolutions: params.conflictResolutions }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/pull', method: 'post' },
    ),
    pushBranch: new APIRequest(
      (params) => axiosAuthClient.post(`${defaultV1Url}/branches/${params.fromBranchName}/push`, { toBranchName: params.toBranchName }),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/push', method: 'post' },
    ),
    importRules: new APIRequest(
      (params) => {
        const formData = new FormData();
        formData.append('file', params.file);
        return axiosAuthClient.post(`${defaultV1Url}/branches/${params.branchName}/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      },
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/import', method: 'post' },
    ),
    importTranslation: new APIRequest(
      (params) => {
        const formData = new FormData();
        formData.append('file', params.file);
        return axiosAuthClient.post(`${defaultV1Url}/branches/${params.branchName}/import/translations`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(({ data }) => data);
      },
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/import/translations', method: 'post' },
    ),
    exportTranslation: new APIRequest(
      (params) => axiosAuthClient.get(`${defaultV1Url}/branches/${params.branchName}/export/translations`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/export/translations', method: 'get' },
    ),
    deletePrivateBranch: new APIRequest(
      (params) => axiosAuthClient.delete(`${defaultV1Url}/branches/${encodeURI(params.privateBranchName)}`),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+', method: 'delete' },
    ),
    getBranchHistory: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<BranchCommit>>(`${defaultV1Url}/branches/${params.branchName}/history`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/history', method: 'get' },
    ),
    getJoiningCommitHistory: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<BranchCommit>>(`${defaultV1Url}/branches/${params.branchName}/history/joining/${params.commitId}`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/history\\/joining\\/[a-zA-Z0-9\\-]+\\', method: 'get' },
    ),
    exportBranchCommit: new APIRequest(
      (params) => axiosAuthClient.get(`${defaultV1Url}/public-branches/${params.publicBranchId}/history/${params.commitId}`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/public-branches\\/[a-zA-Z0-9\\-]+\\/history\\/[a-zA-Z0-9\\-]+\\', method: 'get' },
    ),
    getCommitSummary: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<CommitSummaryEntry>>(`${defaultV1Url}/commits/${params.commitId}/summaries`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/commits\\/[a-zA-Z0-9\\-]+\\/summaries', method: 'get' },
    ),
    getTagDetails: new APIRequest(
        (params) => axiosAuthClient.get<TagDetails>(`${defaultV1Url}/branches/${params.branchName}/tags/${params.tagName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[a-zA-Z0-9\\-]+\\/tags\\/[a-zA-Z0-9\\-]+', method: 'get' },
    ),
    getDeploymentTags: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<DeploymentTag>>(`${defaultV1Url}/deployments`, { params: formatGetDeploymentTagsParams(params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/deployments', method: 'get' },
    ),
    createDeploymentTag: new APIRequest(
      (params) => axiosAuthClient.post<DeploymentTag>(`${defaultV1Url}/branches/${params.branchName}/tags/${params.tagName}`).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/[^\\/]+\\/tags\\/[^\\/]+', method: 'post' },
    ),
    exportDeploymentTag: new APIRequest(
      (params) => axiosAuthClient.get(`${defaultV1Url}/branches/${params.branchName}/tags/${params.tagName}/export`,  { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/[^\\/]+\\/tags\\/[^\\/]+\\/export', method: 'get' },
    ),
    getDeploymentHistory: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<DeploymentHistoryEntry>>(`${defaultV1Url}/deployments/publicBranchName/${params.publicBranchName}/tags/${params.tagName}/environments/${params.environmentName}/history`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/public-branches\\/[a-zA-Z0-9\\-]+\\/tags\\/[a-zA-Z0-9\\-]+\\/environments\\/[a-zA-Z0-9\\-]+\\/deployments', method: 'get' },
    ),
    setDeploymentState: new APIRequest(
      (params) => axiosAuthClient.post(`${defaultV1Url}/deployments`, params).then(({ data }) => data),
      { urlRegexp: '\\/api\\/deployments\\', method: 'post' },
    ),
    getLineTests: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<LineTestRun>>(`${defaultV1Url}/branches/rule/lines/tests/summary?forkedFromBranchName=${params.publicBranchName}`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/branches\\/rule\\/lines\\/tests\\/summary', method: 'get' },
    ),
    runLineTest: new APIRequest(
      (params) => axiosAuthClient.post(`${defaultV1Url}/branches/rule/lines/tests/run?branchName=${params.privateBranchName}`),
      { urlRegexp: '\\/api\\/branches\\/rule\\/lines\\/tests\\/run', method: 'post' },
    ),
    exportLineTests: new APIRequest(
      (params) => axiosAuthClient.get(`${defaultV1Url}/branches/rule/lines/tests/export?commitId=${params.commitId}&jobId=${params.jobId}`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/branches\\/rule\\/lines\\/tests\\/export', method: 'get' },
    ),
    importMetadata: new APIRequest(
      (params) => {
        const formData = new FormData();
        formData.append('data', params.file);
        return axiosAuthClient.put(`${defaultV1Url}/public-branches/${params.publicBranchId}/metadata`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      },
      { urlRegexp: '\\/api\\/public-branches\\/[a-zA-Z0-9\\-]+\\/metadata', method: 'put' },
    ),
    exportMetadata: new APIRequest(
      (publicBranchId) => axiosAuthClient.get(`${defaultV1Url}/public-branches/${publicBranchId}/metadata`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/public-branches\\/[a-zA-Z0-9\\-]+\\/metadata', method: 'get' },
    ),
    getMetadataList: new APIRequest(
      (publicBranchId) => axiosAuthClient.get(`${defaultV1Url}/public-branches/${publicBranchId}/metadata/list`),
      { urlRegexp: '\\/api\\/public-branches\\/[a-zA-Z0-9\\-]+\\/metadata/list', method: 'get' },
    ),
    getLinkTags: new APIRequest(
      (params) => axiosAuthClient.get<Paginated<LinkTag>>(`${defaultV1Url}/public-branches/${params.publicBranchId}/linkTags`, { params: formatRequestParams(params.params) }).then(({ data }) => data),
      { urlRegexp: '\\/api\\/public-branches\\/[a-zA-Z0-9\\-]+\\/linkTags', method: 'get' },
    ),
    selectLinkTagMetadata: new APIRequest(
      (params) => axiosAuthClient.put(`${defaultV1Url}/public-branches/${params.publicBranchId}/linkTags/${params.tag}`, params.selectedMetadata).then(({ data }) => data),
      { urlRegexp: '\\/api\\/public-branches\\/[a-zA-Z0-9\\-]+\\/linkTags\\/[a-zA-Z0-9\\-]+', method: 'put' },
    ),

    exportCommit: new APIRequest(
      (commitId) => axiosAuthClient.get(`${defaultV1Url}/commits/${commitId}/export`, { responseType: 'blob' }).then(getFile),
      { urlRegexp: '\\/api\\/commits\\/[a-zA-Z0-9\\-]+\\/export', method: 'get' },
    ),
  };

  

  return api;
};
